import { Card, CardContent } from "../ui/card";
import { ImageWithFallback } from "../figma/ImageWithFallback";
import { Users, Award, Heart, Target } from 'lucide-react';

const values = [
  {
    icon: Heart,
    title: "Nurturing Care",
    description: "We provide a loving, supportive environment where every child feels safe and valued."
  },
  {
    icon: Target,
    title: "Excellence",
    description: "We strive for the highest standards in early childhood education and development."
  },
  {
    icon: Users,
    title: "Community",
    description: "We build strong partnerships with families and our local community."
  },
  {
    icon: Award,
    title: "Growth",
    description: "We celebrate each child's unique journey and individual achievements."
  }
];

const team = [
  {
    name: "Dr. Sarah Mitchell",
    role: "Director & Founder",
    experience: "15+ years",
    avatar: "SM"
  },
  {
    name: "Emily Rodriguez",
    role: "Head Teacher",
    experience: "12+ years", 
    avatar: "ER"
  },
  {
    name: "Michael Chen",
    role: "Creative Arts Coordinator",
    experience: "8+ years",
    avatar: "MC"
  },
  {
    name: "Lisa Johnson",
    role: "Early Learning Specialist",
    experience: "10+ years",
    avatar: "LJ"
  }
];

export function About() {
  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-purple-50 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <span className="bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-medium">
              About Eyes Montessori London Kains
            </span>
            <h1 className="text-5xl font-bold text-gray-800 mt-4 mb-6">
              Where Learning Meets Joy
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed">
              For over a decade, Eyes Montessori London Kains has been dedicated to creating exceptional learning experiences 
              for children. Our innovative approach combines play-based learning with academic excellence, 
              fostering curiosity, creativity, and confidence in every child.
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <ImageWithFallback
                src="https://images.unsplash.com/photo-1574132190990-cfd62178bb1c?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxraWRzJTIwY2xhc3Nyb29tJTIwbGVhcm5pbmd8ZW58MXx8fHwxNzU2OTA3NDU3fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
                alt="Children learning together"
                className="rounded-2xl shadow-xl w-full h-auto"
              />
            </div>
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">Our Mission</h2>
                <p className="text-gray-600 leading-relaxed">
                  To provide exceptional early childhood education that nurtures each child's unique 
                  potential, builds strong foundations for lifelong learning, and creates joyful 
                  memories that last forever.
                </p>
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">Our Vision</h2>
                <p className="text-gray-600 leading-relaxed">
                  To be the leading early learning center where children discover their passions, 
                  develop confidence, and grow into compassionate, creative, and capable individuals 
                  ready for the world ahead.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-6">Our Core Values</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              These fundamental principles guide everything we do and shape the culture 
              of learning and growth at Eyes Montessori London Kains.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="text-center shadow-lg hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-gradient-to-r from-orange-400 to-pink-400 rounded-full flex items-center justify-center mx-auto mb-6">
                    <value.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-6">Meet Our Team</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our passionate educators bring years of experience and genuine care 
              to create the best learning environment for your child.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <Card key={index} className="text-center shadow-lg hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-8">
                  <div className="w-20 h-20 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full flex items-center justify-center mx-auto mb-6">
                    <span className="text-white font-bold text-xl">{member.avatar}</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">{member.name}</h3>
                  <p className="text-orange-500 font-medium mb-2">{member.role}</p>
                  <p className="text-gray-600 text-sm">{member.experience} experience</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-gradient-to-r from-orange-400 via-pink-400 to-purple-500">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 text-center text-white">
            <div>
              <div className="text-4xl font-bold mb-2">500+</div>
              <div className="text-lg opacity-90">Happy Children</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">10+</div>
              <div className="text-lg opacity-90">Years Experience</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">25+</div>
              <div className="text-lg opacity-90">Expert Teachers</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">15+</div>
              <div className="text-lg opacity-90">Learning Programs</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}